/** Pure level-access policy. DOM, URL navigation, and data loading live elsewhere. */

import type { SaveData } from '@/systems/save';

export interface LevelGate {
  id: string;
  unlockAfter: string | null;
  available: boolean;
}

export type LevelAccess = 'unlocked' | 'locked' | 'unavailable';
export type LevelRequestReason = 'none' | 'unknown' | 'locked' | 'unavailable';

export interface LevelRequestResolution<T extends LevelGate> {
  entry: T | null;
  reason: LevelRequestReason;
}

export function levelAccess(level: LevelGate, save: SaveData): LevelAccess {
  if (!level.available) return 'unavailable';
  if (level.unlockAfter === null) return 'unlocked';
  return save.levels[level.unlockAfter]?.completed === true ? 'unlocked' : 'locked';
}

export function resolveLevelRequest<T extends LevelGate>(
  levels: readonly T[],
  requestedId: string | null,
  save: SaveData,
): LevelRequestResolution<T> {
  if (requestedId === null) return { entry: null, reason: 'none' };
  const entry = levels.find((level) => level.id === requestedId);
  if (!entry) return { entry: null, reason: 'unknown' };

  const access = levelAccess(entry, save);
  if (access !== 'unlocked') return { entry: null, reason: access };
  return { entry, reason: 'none' };
}
